import type { ReactElement } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useDispatch } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import Sentry from "@similarweb/sentry-logger";
import { SegmentedControl, type SegmentedControlValue } from "@similarweb/ui-components";

import { showErrorToast } from "app/actions/toast_actions";
import type { ChipGroupItem } from "app/pages/sales-intelligence/common-components/filters-panel/components/ChipsGroup/ChipsGroup";
import ChipsGroup from "app/pages/sales-intelligence/common-components/filters-panel/components/ChipsGroup/ChipsGroup";
import { FilterConditionDialog } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterConditionDialog/FilterConditionDialog";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import type { ISiteTagsDto } from "app/pages/sales-intelligence/domain/leads/types/filter-values";
import { hasSameSiteTagsObject } from "app/pages/sales-intelligence/domain/leads/utils/site-tags-operations";
import leadsSearchFiltersI18nKeys from "app/pages/sales-intelligence/modules/leads-search-filters/utils/i18n";
import InclusionTechnologyRadioButtons from "app/pages/sales-intelligence/modules/technology/components/InclusionTechnologyRadioButtons/InclusionTechnologyRadioButtons";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import {
    type InclusionOption,
    inclusionOptionMap,
    operatorOptionMap,
} from "app/pages/sales-intelligence/types/commonMaps";
import { downloadFile } from "app/pages/sales-intelligence/utils/downloadFile";

import useDebouncedValue from "../../../../../hooks/useDebouncedValue";
import { SiteTagsFilterValue } from "../../../../../models/SiteTagsFilter/SiteTagsFilterValue";
import useSiteTagsBulkValidationQuery from "../../../../../queries/useSiteTagsBulkValidationQuery";
import { getUnavailableSiteTagValues } from "../../../../../utils/getUnavailableInclusionFilterValues";
import { hasDuplicateInclusionCondition } from "../../../../../utils/hasDuplicateInclusionCondition";
import BulkDownloadBanner from "../../SiteTagsBulkInput/BulkDownloadBanner";
import SiteTagsBulkInput from "../../SiteTagsBulkInput/SiteTagsBulkInput";
import SiteTagsBulkInvalidNotification from "../../SiteTagsBulkInput/SiteTagsBulkInvalidNotification";
import {
    areSiteTagListsEqual,
    getInvalidBulkSiteTags,
    countUniquePreparedSiteTags,
    MAX_SITE_TAGS_BULK_VALIDATION,
    mergeSiteTagsBulk,
    parseSiteTagsBulkValue,
    prepareSiteTagsForValidation,
} from "../../SiteTagsBulkInput/utils";
import ValuesDropdown from "../../ValuesDropdown";

import {
    StyledBulkInputSection,
    StyledBulkModeBannersSection,
    StyledSiteTagsBulkValidationFailed,
    StyledInputModeFrame,
    StyledModalChipsSection,
    StyledModalContent,
    StyledSegmentedControlContainer,
    StyledValuesFieldContainer,
} from "./styles";

const INIT_SITE_TAGS_MODAL_VALUE: ISiteTagsDto = {
    inclusion: inclusionOptionMap.includeOnly,
    values: [],
};

const INPUT_MODE_SINGLE_INDEX = 0;
const INPUT_MODE_BULK_INDEX = 1;

interface ISiteTagsModalProps {
    isOpen: boolean;
    selected: ISiteTagsDto | null;
    value: ISiteTagsDto[] | null;
    isExcludeAvailable: boolean;
    onClose: () => void;
    onCancel: () => void;
    onApply: (item: ISiteTagsDto, selected?: ISiteTagsDto | null) => void;
}

export const SiteTagsModal = ({
    isOpen,
    selected,
    value,
    isExcludeAvailable,
    onClose,
    onCancel,
    onApply,
}: ISiteTagsModalProps): ReactElement => {
    const { translate } = useTranslate();
    const dispatch = useDispatch();
    const [siteTagsItem, setSiteTagsItem] = useState<ISiteTagsDto>(INIT_SITE_TAGS_MODAL_VALUE);
    const [inputModeIndex, setInputModeIndex] = useState(INPUT_MODE_SINGLE_INDEX);
    const [bulkRawValue, setBulkRawValue] = useState("");
    const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);

    const parsedBulkTags = useMemo(() => parseSiteTagsBulkValue(bulkRawValue), [bulkRawValue]);
    const uniqueBulkTagCount = useMemo(() => countUniquePreparedSiteTags(parsedBulkTags), [parsedBulkTags]);
    const debouncedParsedBulkTags = useDebouncedValue(parsedBulkTags);
    const preparedBulkTags = useMemo(() => prepareSiteTagsForValidation(parsedBulkTags), [parsedBulkTags]);
    const debouncedPreparedBulkTags = useMemo(
        () => prepareSiteTagsForValidation(debouncedParsedBulkTags),
        [debouncedParsedBulkTags],
    );
    const isBulkMode = inputModeIndex === INPUT_MODE_BULK_INDEX;
    const isBulkValidationPending = !areSiteTagListsEqual(preparedBulkTags, debouncedPreparedBulkTags);

    const {
        data: validatedBulkTags = [],
        isFetching: isValidatingBulkTags,
        isError: isBulkValidationError,
        isSuccess: isBulkValidationSuccess,
    } = useSiteTagsBulkValidationQuery({
        tags: debouncedPreparedBulkTags,
        enabled: isBulkMode && isOpen,
    });

    const invalidBulkTagsCount = useMemo(() => {
        if (
            isValidatingBulkTags ||
            isBulkValidationPending ||
            isBulkValidationError ||
            !isBulkValidationSuccess ||
            debouncedPreparedBulkTags.length === 0
        ) {
            return 0;
        }

        return getInvalidBulkSiteTags(debouncedPreparedBulkTags, validatedBulkTags).length;
    }, [
        debouncedPreparedBulkTags,
        isBulkValidationError,
        isBulkValidationPending,
        isBulkValidationSuccess,
        isValidatingBulkTags,
        validatedBulkTags,
    ]);

    const showBulkValidationFailedMessage =
        isBulkMode &&
        debouncedPreparedBulkTags.length > 0 &&
        !isBulkValidationPending &&
        !isValidatingBulkTags &&
        isBulkValidationError;

    const isBulkTextAwaitingValidMerge = useMemo((): boolean => {
        if (!bulkRawValue.trim()) {
            return false;
        }

        if (preparedBulkTags.length === 0) {
            return true;
        }

        if (uniqueBulkTagCount > MAX_SITE_TAGS_BULK_VALIDATION) {
            return true;
        }

        return isBulkValidationPending || isValidatingBulkTags || isBulkValidationError;
    }, [
        bulkRawValue,
        isBulkValidationError,
        isBulkValidationPending,
        isValidatingBulkTags,
        preparedBulkTags.length,
        uniqueBulkTagCount,
    ]);

    const inputModeSegmentValues = useMemo(
        (): SegmentedControlValue[] => [
            {
                value: "single",
                label: translate(leadsSearchFiltersI18nKeys.siteTagsInputModeSingle()),
            },
            {
                value: "bulk",
                label: translate(leadsSearchFiltersI18nKeys.siteTagsInputModeBulk()),
            },
        ],
        [translate],
    );

    const initValue = () => {
        setSiteTagsItem(INIT_SITE_TAGS_MODAL_VALUE);
        setInputModeIndex(INPUT_MODE_SINGLE_INDEX);
        setBulkRawValue("");
    };

    const handleSetInclusion = (inclusionOption: InclusionOption) => {
        setSiteTagsItem((prev) => ({
            ...prev,
            inclusion: inclusionOption,
        }));
    };

    const handleCancel = () => {
        onCancel();
        initValue();
    };

    const getItemWithBulkTags = (item: ISiteTagsDto): ISiteTagsDto => {
        if (!bulkRawValue.trim()) {
            return item;
        }

        return mergeSiteTagsBulk({
            item,
            preparedTags: preparedBulkTags,
            validatedTags: validatedBulkTags,
        });
    };

    const reportSiteTagsBulkError = useCallback(
        (message: string, error: unknown) => {
            Sentry.errorTracker({ message, e: error, level: "error" });
            dispatch(showErrorToast(translate("si.common.error.some-error-occurred")));
        },
        [dispatch, translate],
    );

    const handleOnApply = () => {
        const itemToApply = isBulkMode && bulkRawValue.trim() ? getItemWithBulkTags(siteTagsItem) : siteTagsItem;

        onApply(itemToApply, selected);
        initValue();
    };

    const handleValueChange = (values: readonly string[]) => {
        setSiteTagsItem((prev) => {
            const nextValue = new SiteTagsFilterValue(prev).with({ values, inclusion: prev.inclusion });

            return nextValue ?? { ...prev, values: [] };
        });
    };

    const handleChipDelete = (chip: ChipGroupItem) => {
        setSiteTagsItem((prev) => ({
            ...prev,
            values: prev.values.filter((tag) => tag !== chip.type),
        }));
    };

    const handleInputModeChange = (nextIndex: number) => {
        if (nextIndex === INPUT_MODE_BULK_INDEX) {
            setInputModeIndex(nextIndex);
            return;
        }

        if (isBulkMode && bulkRawValue.trim()) {
            if (isBulkTextAwaitingValidMerge) {
                return;
            }

            setSiteTagsItem((prev) => getItemWithBulkTags(prev));
            setBulkRawValue("");
        }

        setInputModeIndex(nextIndex);
    };

    const handleDownloadCsv = async () => {
        setIsDownloadingCsv(true);

        try {
            const { url } = await salesApiService.leadsSearchFilters.fetchSiteTagsDownloadUrl();
            downloadFile("site-tags.csv", url);
        } catch (error) {
            reportSiteTagsBulkError("SiteTagsModal: site tags CSV download failed", error);
        } finally {
            setIsDownloadingCsv(false);
        }
    };

    const isApplyDisabled = () => {
        if (isBulkMode && bulkRawValue.trim()) {
            if (isBulkTextAwaitingValidMerge) {
                return true;
            }

            if (invalidBulkTagsCount > 0) {
                return true;
            }

            const previewItem = getItemWithBulkTags(siteTagsItem);

            if (previewItem.values.length === 0) {
                return true;
            }

            return hasSameSiteTagsObject(previewItem, selected);
        }

        const conditions = value ?? [];

        return (
            siteTagsItem.values.length === 0 ||
            hasSameSiteTagsObject(siteTagsItem, selected) ||
            hasDuplicateInclusionCondition(conditions, siteTagsItem, selected, hasSameSiteTagsObject)
        );
    };

    useEffect(() => {
        if (isOpen && selected) {
            setSiteTagsItem(selected);
        }

        if (!isOpen) {
            initValue();
        }
    }, [isOpen, selected]);

    const disabledSiteTagValues = useMemo(() => getUnavailableSiteTagValues(value ?? [], selected), [selected, value]);

    const chipItems = siteTagsItem.values.map((tag) => ({ name: tag, type: tag }));

    return (
        <FilterConditionDialog
            isOpen={isOpen}
            onRequestClose={onClose}
            dataAutomation="site-tags-modal"
            title={<I18n translatable={{ key: "si.lead_gen_filters.filters.siteTags.modal.title" }} />}
            description={<I18n translatable={{ key: "si.lead_gen_filters.filters.siteTags.modal.subtitle" }} />}
            onCancel={handleCancel}
            onApply={handleOnApply}
            isApplyDisabled={isApplyDisabled()}
            allowContentOverflow
        >
            <StyledModalContent>
                <StyledSegmentedControlContainer data-automation="site-tags-input-mode-toggle">
                    <SegmentedControl
                        values={inputModeSegmentValues}
                        selectedIndex={inputModeIndex}
                        onChange={handleInputModeChange}
                    />
                </StyledSegmentedControlContainer>

                <StyledInputModeFrame>
                    <InclusionTechnologyRadioButtons
                        isExcludeAvailable={isExcludeAvailable}
                        selectedInclusion={siteTagsItem.inclusion}
                        onSelect={handleSetInclusion}
                    />

                    <StyledModalChipsSection>
                        {chipItems.length > 0 ? (
                            <ChipsGroup
                                chipItems={chipItems}
                                divider={operatorOptionMap.or}
                                onDelete={handleChipDelete}
                            />
                        ) : null}
                    </StyledModalChipsSection>

                    {isBulkMode ? (
                        <StyledBulkInputSection>
                            <SiteTagsBulkInput
                                value={bulkRawValue}
                                onChange={setBulkRawValue}
                            />
                        </StyledBulkInputSection>
                    ) : (
                        <StyledValuesFieldContainer>
                            <ValuesDropdown
                                values={siteTagsItem.values}
                                disabledValues={disabledSiteTagValues}
                                onChange={handleValueChange}
                            />
                        </StyledValuesFieldContainer>
                    )}
                </StyledInputModeFrame>

                {isBulkMode ? (
                    <StyledBulkModeBannersSection>
                        {showBulkValidationFailedMessage ? (
                            <StyledSiteTagsBulkValidationFailed data-automation="site-tags-bulk-validation-error">
                                {translate(leadsSearchFiltersI18nKeys.siteTagsBulkValidationFailed())}
                            </StyledSiteTagsBulkValidationFailed>
                        ) : null}
                        {invalidBulkTagsCount > 0 ? (
                            <SiteTagsBulkInvalidNotification invalidCount={invalidBulkTagsCount} />
                        ) : null}
                        <BulkDownloadBanner
                            isLoading={isDownloadingCsv}
                            onDownload={handleDownloadCsv}
                        />
                    </StyledBulkModeBannersSection>
                ) : null}
            </StyledModalContent>
        </FilterConditionDialog>
    );
};
